import { useState } from "react";
import { Link } from "react-router-dom";
import { HardHat, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/AppLayout";
import { useApi, errMsg } from "@/lib/api";
import { useMe } from "@/context/MeContext";

const OPTIONS = [
  { v: "technician", icon: HardHat, t: "I'm a technician", d: "Electrician, mechanic, IRATA — I build my profile and apply for jobs." },
  { v: "company", icon: Building2, t: "I'm a company", d: "I post job offers and review candidates." },
];

export default function Onboarding() {
  const api = useApi();
  const { me, setMe } = useMe();
  const [role, setRole] = useState("technician");
  const [name, setName] = useState(me?.name || "");
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { setMe(await api("post", "/me/onboard", { role, name, accept_terms: accept })); toast.success("Welcome to WindWorkHub!"); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12">
      <Logo />
      <form onSubmit={submit} className="rise mt-10 w-full max-w-xl panel p-6 sm:p-8" data-testid="onboarding-form">
        <p className="eyebrow">Step 1 / 1</p>
        <h1 className="mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight">Who are you?</h1>
        <div className="mt-6 grid sm:grid-cols-2 gap-3" data-testid="auth-role-select">
          {OPTIONS.map((o) => (
            <button type="button" key={o.v} onClick={() => setRole(o.v)} data-testid={`role-option-${o.v}`}
              className={`text-left p-4 rounded-md border-2 transition-colors ${role === o.v ? "border-sky-600 bg-sky-50" : "border-slate-200 hover:border-slate-300"}`}>
              <o.icon className={role === o.v ? "text-sky-700" : "text-slate-500"} />
              <p className="mt-3 font-bold">{o.t}</p>
              <p className="mt-1 text-sm text-slate-600">{o.d}</p>
            </button>
          ))}
        </div>
        <div className="mt-6 space-y-2">
          <Label htmlFor="onb-name">{role === "company" ? "Company name" : "Full name"}</Label>
          <Input id="onb-name" data-testid="onboarding-name-input" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} />
        </div>
        <label className="mt-6 flex items-start gap-2 text-sm text-slate-700">
          <Checkbox checked={accept} onCheckedChange={(v) => setAccept(!!v)} data-testid="onboarding-terms-checkbox" className="mt-0.5" />
          <span>I accept the <Link to="/terms" target="_blank" className="text-sky-700 underline">Terms of Service</Link> and the <Link to="/privacy" target="_blank" className="text-sky-700 underline">Privacy Policy</Link>.</span>
        </label>
        <Button type="submit" disabled={busy || name.trim().length < 2 || !accept} data-testid="onboarding-submit-button" className="mt-6 w-full h-11">
          {busy ? "Saving..." : "Continue"}
        </Button>
        <p className="mt-3 text-xs text-slate-500">The role can't be changed later.</p>
      </form>
    </div>
  );
}
